/* eslint-disable react/no-array-index-key */
import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TbLogout2 } from 'react-icons/tb';

import { useKeycloak } from '@cccom/auth/keycloak';
import { version } from '@cccom/package';
import { useDate } from '@cccom/shared/settings';
import { CCAvatar } from '@cccom/shared/ui';

import { Button } from '@cccom/ui/src/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cccom/ui/src/components/shadcn/dropdown-menu';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@cccom/ui/src/components/shadcn/tooltip';

type ProfileMenuProps = {
  isCollapsed?: boolean;
  children?: ReactNode;
  menuItems?: ReactNode[];
};

export function ProfileMenu({
  isCollapsed,
  children,
  menuItems,
}: ProfileMenuProps) {
  const [t] = useTranslation();
  const { keycloak, email, name } = useKeycloak();
  const { timezoneOffset } = useDate();
  const majorVersion = version.split('.')[0];

  // Tailwind spacing
  const avatarWidth = '32px'; // 8 * 4px
  const spacing = '56px'; // (14 + 4) * 4px
  const maxWidth = `calc(var(--sidebar-width) - ${spacing} - ${avatarWidth})`;

  return (
    <Tooltip>
      <TooltipContent
        side="right"
        className={`${!isCollapsed ? 'hidden' : ''} text-xs`}
      >
        <p>{t('profile.user_profile')}</p>
      </TooltipContent>
      <DropdownMenu>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              data-cy="profileMenu"
              className="flex h-20 w-full items-center rounded-none px-6 py-4"
            >
              <div className="flex w-full items-center gap-4">
                <CCAvatar name={name || email} />
                <div
                  className={`flex flex-col items-start ${
                    isCollapsed ? 'absolute opacity-0' : 'relative opacity-100'
                  }`}
                  style={{ maxWidth }}
                >
                  <span className="w-full truncate text-left text-sm">
                    {name}
                  </span>
                  <span className="w-full truncate text-left text-xs text-gray-500">
                    {email}
                  </span>
                </div>
              </div>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>

        <DropdownMenuContent
          side="right"
          align="end"
          className="z-50 mb-1 w-64 p-0"
        >
          <div className="flex flex-col items-start px-6 py-2">
            <span className="text-base font-medium">{name}</span>
            <span className="text-xs text-gray-500">{email}</span>
          </div>

          <DropdownMenuSeparator />

          {children && (
            <>
              <div className="flex flex-col items-start px-6 py-2">
                {children}
              </div>
              <DropdownMenuSeparator />
            </>
          )}
          <div className="flex flex-col items-start px-6 py-2 text-xs text-gray-500">
            <span>{timezoneOffset}</span>
            <span>
              {t('common.labels.version')}: {majorVersion}
            </span>
          </div>

          <DropdownMenuSeparator />

          {menuItems?.map((item, index) => (
            <Fragment key={index}>
              {item}
              <DropdownMenuSeparator />
            </Fragment>
          ))}

          <DropdownMenuItem
            className="cursor-pointer gap-2 px-6 py-2 text-blue-950"
            onClick={() => keycloak.logout()}
          >
            <TbLogout2 className="h-5 w-5 text-blue-950" />
            {t('common.actions.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Tooltip>
  );
}
